import React, { useState, useEffect } from 'react';
import { ApiClient } from '@docplugin/core';
import type { Placeholder, CreatePlaceholderRequest, PlaceholderType } from '@docplugin/core';
import {
  getContentControlTags,
  insertPlaceholderText,
  insertFloatingPlaceholder,
  convertDroppedPlaceholders,
  restyleAllPlaceholders,
  getAllPlaceholderTags,
  selectContentControl,
  setContentControlColor,
  getConditionalTags,
  type ContentControlTag,
} from '../services/wordHelper';
import type { CreateMode, TemplateContext } from '../App';
import { auth, getAuthHeaders, API_URL } from '../services/auth';
import { Can } from './Can';
import './PlaceholderList.css';

const api = new ApiClient({
  baseUrl: API_URL,
  apiKey: 'dp_dev_howden_key123',
  getToken: () => (auth.isAuthenticated() ? auth.getAccessToken() : null),
});

function normalizePlaceholder(raw: Record<string, unknown>): Placeholder & Record<string, unknown> {
  return {
    id: (raw.id ?? raw.Id) as number,
    tenantId: (raw.tenantId ?? raw.TenantId) as number,
    key: (raw.key ?? raw.Key ?? '') as string,
    label: (raw.label ?? raw.Label ?? '') as string,
    type: (raw.type ?? raw.Type ?? 'Text') as PlaceholderType,
    description: (raw.description ?? raw.Description ?? '') as string,
    required: (raw.required ?? raw.Required ?? false) as boolean,
    sampleValue: (raw.sampleValue ?? raw.SampleValue ?? '') as string,
    source: (raw.source ?? raw.Source ?? '') as string,
    createdAt: (raw.createdAt ?? raw.CreatedAt ?? '') as string,
    updatedAt: (raw.updatedAt ?? raw.UpdatedAt ?? '') as string,
    defaultValue: (raw.defaultValue ?? raw.DefaultValue ?? null) as string | null,
    defaultExpression: (raw.defaultExpression ?? raw.DefaultExpression ?? null) as string | null,
    DefaultValue: (raw.defaultValue ?? raw.DefaultValue ?? null) as string | null,
    DefaultExpression: (raw.defaultExpression ?? raw.DefaultExpression ?? null) as string | null,
  };
}

type Tab = 'document' | 'catalog';
const PLACEHOLDER_TYPES: PlaceholderType[] = ['Text', 'Date', 'Number', 'Money', 'Boolean', 'Address', 'List'];
// Backend value (sent in API body) ↔ user-facing label.
const OPERATORS: { value: string; label: string }[] = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'not equals' },
  { value: 'contains', label: 'contains' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

// ── Types ──────────────────────────────────────────────────────────────────

interface KeywordItem { Id: number; Name: string; Keyword?: string; GroupName?: string | null; DocumentTypeGroup?: string | null; DocumentType?: string | null; }

interface ConditionRow { id: number; placeholder: string; operator: string; value: string; insertText: string; }

interface AdvancedCondition {
  Id: number; Name: string;
  Conditions: { placeholder: string; operator: string; value?: string; insertText: string }[];
  ElseText: string;
  Field?: string;
  Operator?: string;
  Value?: string | null;
  ThenText?: string;
  TemplateId?: number | null;
}

// ── Keyword Form ───────────────────────────────────────────────────────────

function KeywordForm({ onClose, onSaved, templateId, currentKeywords }: {
  onClose: () => void;
  onSaved: (keywords: KeywordItem[]) => void;
  templateId?: number;
  currentKeywords: KeywordItem[];
}) {
  const [categories, setCategories] = useState<{ Id: number; Name: string }[]>([]);
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [available, setAvailable] = useState<KeywordItem[]>([]);
  const [selected, setSelected] = useState<KeywordItem[]>(currentKeywords);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [creating, setCreating] = useState(false);

  const loadCategories = async () => {
    const resp = await api.get<{ Id: number; Name: string }[]>('/categories');
    if (resp.success && resp.data) setCategories(resp.data);
  };

  const loadKeywords = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (category) params.set('group', category);
      const resp = await api.get<KeywordItem[]>(`/keywords?${params}`);
      if (resp.success && resp.data) {
        // Use functional update to get latest selected state
        setSelected(prev => {
          const selectedIds = new Set(prev.map(s => s.Id));
          setAvailable(resp.data!.filter(k => !selectedIds.has(k.Id)));
          return prev;
        });
      }
    } finally { setLoading(false); }
  };

  const [createError, setCreateError] = useState('');
  const handleCreateKeyword = async () => {
    if (!newKeyword.trim()) return;
    if (!category) { setCreateError('Select a category first'); return; }
    setCreating(true);
    setCreateError('');
    try {
      const resp = await api.post<KeywordItem>('/keywords', {
        keyword: newKeyword.trim(),
        groupName: category,
        documentType: 'General',
      });
      if (resp.success && resp.data) {
        setSelected(prev => [...prev, resp.data!]);
        setNewKeyword('');
        // Remove from available if it appeared there
        setAvailable(prev => prev.filter(k => k.Id !== resp.data!.Id));
      } else {
        setCreateError((resp.error as any)?.message || 'Failed to create');
      }
    } catch { setCreateError('Keyword may already exist'); }
    finally { setCreating(false); }
  };

  useEffect(() => { loadCategories(); loadKeywords(); }, []);
  useEffect(() => { loadKeywords(); }, [search, category]);

  const moveToSelected = (kw: KeywordItem) => {
    setAvailable(prev => prev.filter(k => k.Id !== kw.Id));
    setSelected(prev => [...prev, kw]);
  };

  const moveAllToSelected = () => {
    setSelected(prev => [...prev, ...available]);
    setAvailable([]);
  };

  const moveToAvailable = (kw: KeywordItem) => {
    setSelected(prev => prev.filter(k => k.Id !== kw.Id));
    setAvailable(prev => [...prev, kw].sort((a, b) => a.Name.localeCompare(b.Name)));
  };

  const moveAllToAvailable = () => {
    setAvailable(prev => [...prev, ...selected].sort((a, b) => a.Name.localeCompare(b.Name)));
    setSelected([]);
  };

  const handleSave = async () => {
    if (!templateId) { onSaved(selected); onClose(); return; }
    setSaving(true);
    try {
      await api.post(`/keywords/by-template/${templateId}`, { keywordIds: selected.map(k => k.Id) });
      onSaved(selected);
      onClose();
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  return (
    <div className="dp-ph__create-form">
      <div className="dp-ph__form-header">
        <span className="dp-ph__form-title">Select Keywords</span>
        <button className="dp-ph__form-close" onClick={onClose}>✕</button>
      </div>
      <p className="dp-ph__form-desc">Select keywords for this template. Keywords help with search and retrieval.</p>

      <input className="dp-ph__search" type="text" placeholder="Search keywords..."
        value={search} onChange={e => setSearch(e.target.value)} style={{ marginBottom: 6 }} />
      {createError && <div style={{ color: '#c62828', fontSize: 11, marginBottom: 4 }}>{createError}</div>}

      <div className="dp-kw__dual">
        <div className="dp-kw__col">
          <div className="dp-kw__col-header">
            Available {loading ? '…' : `(${available.length})`}
            <button className="dp-kw__move-all" onClick={moveAllToSelected} title="Add all">»</button>
          </div>
          <div className="dp-kw__list">
            {available.length === 0
              ? <div className="dp-ph__empty" style={{ padding: 8 }}>No keywords</div>
              : available.map(kw => (
                <div key={kw.Id} className="dp-kw__item" onClick={() => moveToSelected(kw)}>
                  <span>{kw.Name}</span>
                  <span className="dp-kw__add">›</span>
                </div>
              ))
            }
          </div>
        </div>

        <div className="dp-kw__col">
          <div className="dp-kw__col-header">
            Selected ({selected.length})
            <button className="dp-kw__move-all" onClick={moveAllToAvailable} title="Remove all">«</button>
          </div>
          <div className="dp-kw__list">
            {selected.length === 0
              ? <div className="dp-ph__empty" style={{ padding: 8 }}>None selected</div>
              : selected.map(kw => (
                <div key={kw.Id} className="dp-kw__item dp-kw__item--selected" onClick={() => moveToAvailable(kw)}>
                  <span>{kw.Name}</span>
                  <span className="dp-kw__remove">✕</span>
                </div>
              ))
            }
          </div>
        </div>
      </div>

      <div className="dp-ph__form-row" style={{ marginTop: 8 }}>
        <button className="dp-ph__btn" onClick={onClose}>Cancel</button>
        <button className="dp-ph__btn dp-ph__btn--primary" onClick={handleSave}
          disabled={selected.length === 0 || saving}>
          {saving ? 'Saving...' : `Save ${selected.length > 0 ? `(${selected.length})` : ''}`}
        </button>
      </div>
    </div>
  );
}

// ── Advanced Condition Form ────────────────────────────────────────────────

function AdvancedConditionForm({
  placeholders,
  editing,
  onClose,
  onSaved,
  templateId,
}: {
  placeholders: Placeholder[];
  editing: AdvancedCondition | null;
  onClose: () => void;
  onSaved: () => void;
  templateId?: number;
}) {
  const [conditionName, setConditionName] = useState(editing?.Name ?? '');
  const [rows, setRows] = useState<ConditionRow[]>(
    editing?.Conditions?.length
      ? editing.Conditions.map((c, i) => ({ id: i + 1, placeholder: c.placeholder, operator: c.operator, value: c.value ?? '', insertText: c.insertText }))
      : editing?.Field
        ? [{ id: 1, placeholder: editing.Field, operator: editing.Operator || 'equals', value: editing.Value ?? '', insertText: editing.ThenText ?? '' }]
        : [{ id: 1, placeholder: '', operator: 'equals', value: '', insertText: '' }]
  );
  const [elseText, setElseText] = useState(editing?.ElseText ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear any stale "save failed" / "Cannot connect" error as soon as the
  // user starts editing — prevents the banner sticking around after a
  // transient API hiccup. Initial mount no-ops because error starts null.
  useEffect(() => {
    setError(null);
  }, [conditionName, rows, elseText]);

  const addRow = () => {
    setRows(prev => [...prev, { id: Date.now(), placeholder: '', operator: 'equals', value: '', insertText: '' }]);
  };

  const removeRow = (id: number) => {
    setRows(prev => prev.filter(r => r.id !== id));
  };

  const updateRow = (id: number, field: keyof ConditionRow, val: string) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: val } : r));
  };

  const handleSave = async () => {
    if (!conditionName.trim()) return;
    setSaving(true);
    setError(null);
    try {
      // Backend expects flat fields: name, field, operator, value, thenText, elseText
      // The first row is the primary condition; additional rows saved as children
      const primary = rows[0];
      const body = {
        name: conditionName.trim(),
        field: primary.placeholder,
        operator: primary.operator,
        value: primary.value,
        thenText: primary.insertText,
        elseText,
        templateId: templateId || undefined,
      };
      const resp = editing
        ? await api.patch<Record<string, unknown>>(`/advanced-conditions/${editing.Id}`, body)
        : await api.post<Record<string, unknown>>('/advanced-conditions', body);

      if (resp.success) {
        // Also register as a placeholder so it can be inserted into the document
        const condKey = `condition.${conditionName.trim().replace(/\s+/g, '_').toLowerCase()}`;
        try {
          await api.post('/placeholders', {
            key: condKey,
            label: conditionName.trim(),
            type: 'Text',
            description: `Advanced Condition: IF ${primary.placeholder} ${primary.operator} "${primary.value}" THEN "${primary.insertText}"${elseText ? ` ELSE "${elseText}"` : ''}`,
            source: 'advanced-condition',
          });
        } catch { /* placeholder may already exist */ }
        onSaved();
        onClose();
      } else {
        setError(resp.error?.message || 'Failed to save condition');
      }
    } catch {
      setError('Cannot connect to API');
    } finally {
      setSaving(false);
    }
  };

  const phOptions = placeholders.map(p => p.key).filter(Boolean);

  return (
    <div className="dp-ph__create-form">
      <div className="dp-ph__form-header">
        <span className="dp-ph__form-title">{editing ? 'Edit' : 'New'} Advanced Condition</span>
        <button className="dp-ph__form-close" onClick={onClose}>✕</button>
      </div>
      <p className="dp-ph__form-desc">Insert text into the document based on placeholder values at run time.</p>

      {error && <div className="dp-ph__error">{error}</div>}

      <div className="dp-ph__form-row">
        <input className="dp-ph__input" placeholder="Condition name (e.g. TIN display)"
          value={conditionName} onChange={e => setConditionName(e.target.value)} />
      </div>

      <div className="dp-ac__rows">
        {rows.map((row, idx) => (
          <div key={row.id} className="dp-ac__row">
            <div className="dp-ac__row-label">{idx === 0 ? 'IF' : 'ELSE IF'}</div>
            <div className="dp-ac__row-fields">
              <select className="dp-ph__select dp-ac__select--ph"
                value={row.placeholder} onChange={e => updateRow(row.id, 'placeholder', e.target.value)}>
                <option value="">— select placeholder —</option>
                {phOptions.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
              <select className="dp-ph__select dp-ac__select--op"
                value={row.operator} onChange={e => updateRow(row.id, 'operator', e.target.value)}>
                {OPERATORS.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
              </select>
              {!['is_empty', 'is_not_empty'].includes(row.operator) && (
                <input className="dp-ph__input dp-ac__input--val"
                  placeholder="value"
                  value={row.value} onChange={e => updateRow(row.id, 'value', e.target.value)} />
              )}
            </div>
            <div className="dp-ac__then-row">
              <span className="dp-ac__row-label">THEN</span>
              <input className="dp-ph__input" placeholder="insert text..."
                value={row.insertText} onChange={e => updateRow(row.id, 'insertText', e.target.value)} />
              {rows.length > 1 && (
                <button className="dp-ac__remove-row" onClick={() => removeRow(row.id)} title="Remove">✕</button>
              )}
            </div>
          </div>
        ))}

        <button className="dp-ac__add-row" onClick={addRow}>+ Add condition</button>

        <div className="dp-ac__else-row">
          <span className="dp-ac__row-label">ELSE</span>
          <input className="dp-ph__input" placeholder="default text (or leave blank)"
            value={elseText} onChange={e => setElseText(e.target.value)} />
        </div>
      </div>

      <div className="dp-ph__form-row" style={{ marginTop: 8 }}>
        <button className="dp-ph__btn" onClick={onClose}>Cancel</button>
        <button className="dp-ph__btn dp-ph__btn--primary" onClick={handleSave}
          disabled={saving || !conditionName.trim() || rows.some(r => !r.placeholder)}>
          {saving ? 'Saving...' : (editing ? 'Update' : 'Save Condition')}
        </button>
      </div>
    </div>
  );
}

// ── Document Type Selector ────────────────────────────────────────────────

function DocumentTypeSelector({
  groupedTypes,
  loading,
  currentSelection,
  onSelect,
  onClose,
}: {
  groupedTypes: { group: string; types: string[] }[];
  loading: boolean;
  currentSelection: { group: string; type: string } | null;
  onSelect: (selection: { group: string; type: string } | null) => void;
  onClose: () => void;
}) {
  const [pending, setPending] = useState<{ group: string; type: string } | null>(currentSelection);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = React.useRef<HTMLInputElement>(null);

  const handleApply = () => {
    onSelect(pending);
    onClose();
  };

  const handleItemClick = (group: string, type: string) => {
    setPending({ group, type });
    setSearch('');
    setIsOpen(false);
  };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) setTimeout(() => searchRef.current?.focus(), 50);
  };

  const searchLower = search.toLowerCase();
  const filteredGroups = groupedTypes
    .map(({ group, types }) => ({
      group,
      types: types.filter(t =>
        t.toLowerCase().includes(searchLower) ||
        group.toLowerCase().includes(searchLower)
      ),
    }))
    .filter(g => g.types.length > 0);

  return (
    <div className="dp-ph__create-form">
      <div className="dp-ph__form-header">
        <span className="dp-ph__form-title">Select Document Type</span>
        <button className="dp-ph__form-close" onClick={onClose}>&#10005;</button>
      </div>
      <p className="dp-ph__form-desc">Choose the document type for this template.</p>

      {loading ? (
        <div className="dp-ph__status">Loading...</div>
      ) : (
        <div className="dp-dt-search">
          <button
            type="button"
            className="dp-dt-search__trigger"
            onClick={toggleOpen}
          >
            <span className={pending ? 'dp-dt-search__trigger-text' : 'dp-dt-search__trigger-placeholder'}>
              {pending ? `${pending.group} / ${pending.type}` : '-- Select a document type --'}
            </span>
            <span className="dp-dt-search__trigger-arrow">{isOpen ? '\u25B2' : '\u25BC'}</span>
          </button>

          {isOpen && (
            <>
              <div className="dp-dt-search__dropdown">
                <div className="dp-dt-search__search-box">
                  <input
                    ref={searchRef}
                    className="dp-dt-search__search-input"
                    placeholder="Type to filter..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                  {search && (
                    <button className="dp-dt-search__search-clear" onClick={() => setSearch('')}>&#10005;</button>
                  )}
                </div>
                <div className="dp-dt-search__list">
                  {filteredGroups.length === 0 ? (
                    <div className="dp-dt-search__empty">No matches</div>
                  ) : (
                    filteredGroups.map(({ group, types }) => (
                      <div key={group}>
                        <div className="dp-dt-search__group-header">{group}</div>
                        {types.map(type => {
                          const isSelected = pending?.group === group && pending?.type === type;
                          return (
                            <div
                              key={`${group}:::${type}`}
                              className={`dp-dt-search__item${isSelected ? ' dp-dt-search__item--selected' : ''}`}
                              onClick={() => handleItemClick(group, type)}
                            >
                              <span>{type}</span>
                              {isSelected && <span className="dp-dt-search__check">&#10003;</span>}
                            </div>
                          );
                        })}
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="dp-dt-search__backdrop" onClick={() => { setIsOpen(false); setSearch(''); }} />
            </>
          )}
        </div>
      )}

      <div className="dp-ph__form-row" style={{ marginTop: 8 }}>
        <button className="dp-ph__btn" onClick={onClose}>Cancel</button>
        {pending && (
          <button className="dp-ph__btn" style={{ color: '#c62828' }}
            onClick={() => { onSelect(null); onClose(); }}>
            Clear
          </button>
        )}
        <button
          className="dp-ph__btn dp-ph__btn--primary"
          onClick={handleApply}
          disabled={!pending}
        >
          Select
        </button>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

interface PlaceholderListProps {
  createMode: CreateMode;
  onCloseCreate: () => void;
  activeTemplate: TemplateContext | null;
  onTemplateNameChange: (name: string) => void;
}

export function PlaceholderList({ createMode, onCloseCreate, activeTemplate, onTemplateNameChange }: PlaceholderListProps) {
  const [tab, setTab] = useState<Tab>('document');

  // Section open/close
  const [phSectionOpen, setPhSectionOpen] = useState(true);
  const [kwSectionOpen, setKwSectionOpen] = useState(true);
  const [acSectionOpen, setAcSectionOpen] = useState(true);
  const [dtSectionOpen, setDtSectionOpen] = useState(true);
  const [condSectionOpen, setCondSectionOpen] = useState(true);
  const [tplSectionOpen, setTplSectionOpen] = useState(true);
  const [fragSectionOpen, setFragSectionOpen] = useState(false);
  const [fragments, setFragments] = useState<any[]>([]);
  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [qcSectionOpen, setQcSectionOpen] = useState(false);
  const [qcRunning, setQcRunning] = useState(false);
  const [qcResult, setQcResult] = useState<{ score: number; passed: boolean; issues: any[]; summary: string } | null>(null);
  const [templateCategories, setTemplateCategories] = useState<{ Id: number; Name: string }[]>([]);
  const [templateCategoryId, setTemplateCategoryId] = useState<number | null>(null);

  const runQualityCheck = async () => {
    setQcRunning(true);
    setQcResult(null);
    try {
      // Get document text
      let documentText = '';
      try {
        await Word.run(async (context) => {
          const body = context.document.body;
          body.load('text');
          await context.sync();
          documentText = body.text || '';
        });
      } catch { /* ignore */ }

      if (!documentText || documentText.trim().length < 10) {
        setQcResult({ score: 0, passed: false, issues: [{ severity: 'error', category: 'Error', message: 'Could not read document text. Make sure the document has content.' }], summary: 'No content to check' });
        return;
      }

      // Get placeholder tags from the document
      const placeholderTags = uniqueDocTags.map(t => t.tag);

      const resp = await fetch(`${API_URL}/ai/quality-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          templateId: activeTemplate?.id,
          documentText,
          placeholderTags,
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setQcResult(data.data);
        setQcSectionOpen(true);
      }
    } catch (err) {
      setQcResult({ score: 0, passed: false, issues: [{ severity: 'error', category: 'Error', message: 'Quality check failed. Please try again.' }], summary: 'Check failed' });
    } finally {
      setQcRunning(false);
    }
  };

  const loadTemplateCategory = async () => {
    if (!activeTemplate?.id) return;
    try {
      const resp = await api.get<any>(`/templates/${activeTemplate.id}`);
      if (resp.success && resp.data) setTemplateCategoryId(resp.data.CategoryId ?? null);
    } catch { /* ignore */ }
  };

  const handleCategoryChange = async (catId: string) => {
    if (!activeTemplate?.id) return;
    const value = catId === '__none__' ? null : Number(catId);
    setTemplateCategoryId(value);
    try {
      await api.patch(`/templates/${activeTemplate.id}`, { CategoryId: value });
    } catch { /* ignore */ }
  };

  const handleNameSave = async () => {
    if (!editName.trim() || !activeTemplate) return;
    setNameSaving(true);
    try {
      const resp = await api.patch(`/templates/${activeTemplate.id}`, { name: editName.trim() });
      if (resp.success) {
        onTemplateNameChange(editName.trim());
        setEditingName(false);
      }
    } catch { /* ignore */ }
    finally { setNameSaving(false); }
  };

  const handleDiscardDraft = async () => {
    if (!activeTemplate || activeTemplate.versionStatus !== 'Draft') return;
    setDiscarding(true);
    try {
      // Set version status to Superseded and release lock
      const resp = await fetch(
        `${API_URL}/templates/${activeTemplate.id}/versions/${activeTemplate.versionNumber}/discard`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        }
      );
      const data = await resp.json();
      if (data.success !== false) {
        // Update the template context — revert to previous published version or clear
        onTemplateNameChange(activeTemplate.name); // triggers parent re-render
      }
    } catch { /* ignore */ }
    finally { setDiscarding(false); }
  };

  // Document Type section state
  const [groupedTypes, setGroupedTypes] = useState<{ group: string; types: string[] }[]>([]);
  const [selectedDocType, setSelectedDocType] = useState<{ group: string; type: string } | null>(null);
  const [dtLoading, setDtLoading] = useState(false);
  const [changingDocType, setChangingDocType] = useState(false);

  // Catalog state
  const [placeholders, setPlaceholders] = useState<Placeholder[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [rulesMap, setRulesMap] = useState<Record<number, Array<{ Id?: number; RuleType: string; RuleValue: string; ErrorMessage: string; NlpDescription: string }>>>({});
  const [expandedPh, setExpandedPh] = useState<number | null>(null);
  // Which placeholder row is currently showing the type-picker popup.
  const [typePickerPh, setTypePickerPh] = useState<number | null>(null);
  const [popupRuleInput, setPopupRuleInput] = useState('');
  const [popupRuleSaving, setPopupRuleSaving] = useState(false);
  const [popupRuleError, setPopupRuleError] = useState<string | null>(null);
  const [defaultPopupPh, setDefaultPopupPh] = useState<number | null>(null);
  const [defaultPopupValue, setDefaultPopupValue] = useState('');
  const [defaultPopupExpr, setDefaultPopupExpr] = useState('');
  const [defaultPopupSaving, setDefaultPopupSaving] = useState(false);

  const openDefaultPopup = (p: any) => {
    const phId = p.id || p.Id;
    setDefaultPopupPh(phId);
    setDefaultPopupValue(p.defaultValue || p.DefaultValue || '');
    setDefaultPopupExpr(p.defaultExpression || p.DefaultExpression || '');
  };

  const saveDefault = async () => {
    if (!defaultPopupPh) return;
    setDefaultPopupSaving(true);
    try {
      const body: Record<string, unknown> = {};
      const lower = defaultPopupValue.trim().toLowerCase();
      if (lower === 'today' || lower === 'now') {
        body.defaultExpression = lower;
        body.defaultValue = null;
      } else {
        body.defaultValue = defaultPopupValue.trim() || null;
        body.defaultExpression = null;
      }
      await api.patch(`/placeholders/${defaultPopupPh}`, body);
      // Update local state
      setPlaceholders(prev => prev.map(p => {
        const pid = (p as any).id || (p as any).Id;
        if (pid === defaultPopupPh) {
          return { ...p, defaultValue: body.defaultValue as any, defaultExpression: body.defaultExpression as any,
            DefaultValue: body.defaultValue as any, DefaultExpression: body.defaultExpression as any };
        }
        return p;
      }));
      setDefaultPopupPh(null);
    } catch { /* best effort */ }
    finally { setDefaultPopupSaving(false); }
  };

  const addRuleFromPopup = async (phId: number, description: string) => {
    if (!description || popupRuleSaving) return;
    setPopupRuleSaving(true);
    setPopupRuleError(null);
    try {
      const parseResp = await fetch(`${API_URL}/placeholder-rules/parse-nlp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ placeholderId: phId, description }),
      });
      const parseData = await parseResp.json();
      if (!parseResp.ok || !parseData.success) {
        throw new Error(parseData.error?.message || `Parse failed (HTTP ${parseResp.status})`);
      }
      const rules = parseData.data?.rules || [];

      if (rules.length === 0) {
        setPopupRuleError(`Couldn't parse "${description}" into a rule. Try e.g. "max 100 characters" or "required".`);
        return;
      }

      const newRules: any[] = [];
      const failures: string[] = [];
      for (const rule of rules) {
        const saveResp = await fetch(`${API_URL}/placeholder-rules`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            placeholderId: phId, ruleType: rule.ruleType, ruleValue: rule.ruleValue,
            errorMessage: rule.errorMessage, nlpDescription: description,
          }),
        });
        const saved = await saveResp.json();
        if (saved.success && saved.data) newRules.push(saved.data);
        else failures.push(saved.error?.message || `HTTP ${saveResp.status}`);
      }
      if (newRules.length > 0) {
        setRulesMap(prev => ({ ...prev, [phId]: [...(prev[phId] || []), ...newRules] }));
        setPopupRuleInput('');
      }
      if (failures.length > 0) {
        setPopupRuleError(`Saved ${newRules.length}/${rules.length}. Error: ${failures[0]}`);
      }
    } catch (err: any) {
      setPopupRuleError(err?.message || 'Failed to add rule');
    } finally {
      setPopupRuleSaving(false);
    }
  };
  const [search, setSearch] = useState('');

  // Document state
  const [docTags, setDocTags] = useState<ContentControlTag[]>([]);
  const [docLoading, setDocLoading] = useState(false);

  // Keywords section state
  const [savedKeywords, setSavedKeywords] = useState<KeywordItem[]>([]);

  // Advanced Conditions section state
  const [conditions, setConditions] = useState<AdvancedCondition[]>([]);
  const [conditionsLoading, setConditionsLoading] = useState(false);
  const [editingCondition, setEditingCondition] = useState<AdvancedCondition | null>(null);

  // Conditional section visual toggle state
  const [conditionalTags, setConditionalTags] = useState<Array<{ tag: string; key: string; type: 'IF' | 'UNLESS'; level: number }>>([]);
  const [conditionToggles, setConditionToggles] = useState<Record<string, boolean>>({});

  // Placeholder create form state
  const [newDescription, setNewDescription] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newType, setNewType] = useState<PlaceholderType>('Text');
  const [newRequired, setNewRequired] = useState(false);
  const [newSample, setNewSample] = useState('');
  const [newDefault, setNewDefault] = useState('');
  const [newRule, setNewRule] = useState('');
  const [newRules, setNewRules] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [insertedTag, setInsertedTag] = useState<string | null>(null);

  const togglePhDetails = async (phId: number) => {
    if (expandedPh === phId) { setExpandedPh(null); return; }
    setExpandedPh(phId);
    if (!rulesMap[phId]) {
      try {
        const resp = await fetch(`${API_URL}/placeholder-rules?placeholderId=${phId}`, {
          headers: { ...getAuthHeaders() },
        });
        const data = await resp.json();
        setRulesMap(prev => ({ ...prev, [phId]: data.data || [] }));
      } catch { setRulesMap(prev => ({ ...prev, [phId]: [] })); }
    }
  };

  const loadCatalog = async () => {
    setCatalogLoading(true);
    setCatalogError(null);
    try {
      const resp = await api.get<Record<string, unknown>[]>('/placeholders');
      if (resp.success && resp.data) {
        setPlaceholders(resp.data.map(normalizePlaceholder));
      } else {
        setCatalogError(resp.error?.message || 'Failed to load placeholders');
      }
    } catch {
      setCatalogError('Cannot connect to API. Is the server running?');
    } finally {
      setCatalogLoading(false);
    }
  };

  const loadConditions = async () => {
    setConditionsLoading(true);
    try {
      // Only load conditions if a template is active — don't load all conditions globally
      if (!activeTemplate?.id) {
        setConditions([]);
        return;
      }
      const resp = await api.get<AdvancedCondition[]>(`/advanced-conditions?templateId=${activeTemplate.id}`);
      if (resp.success && resp.data) setConditions(resp.data);
    } catch { /* ignore */ }
    finally { setConditionsLoading(false); }
  };

  const scanDocument = async () => {
    setDocLoading(true);
    try {
      // Merged scan: content controls + floating-shape {tag} tokens, so both
      // placeholder types show up in the "In Document" tab count.
      setDocTags(await getAllPlaceholderTags());
    } catch { /* ignore */ }
    finally { setDocLoading(false); }
  };

  const loadGroupedTypes = async () => {
    setDtLoading(true);
    try {
      // Sourced from the DocumentTypes master table (see
      // /document-types/grouped). The old /keywords/grouped-types endpoint
      // inferred document types from Keyword rows — fragile and produced
      // ghost entries when a keyword had a blank DocumentType. Admins now
      // manage the catalog directly in the workspace's Document Types page.
      const resp = await api.get<{ group: string; types: string[] }[]>('/document-types/grouped');
      if (resp.success && resp.data) setGroupedTypes(resp.data);
    } catch { /* ignore */ }
    finally { setDtLoading(false); }
  };

  const loadTemplateKeywords = async () => {
    if (!activeTemplate?.id) return;
    try {
      const resp = await api.get<KeywordItem[]>(`/keywords/by-template/${activeTemplate.id}`);
      if (resp.success && resp.data) setSavedKeywords(resp.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadCatalog(); scanDocument(); loadConditions(); loadGroupedTypes(); loadTemplateKeywords(); loadTemplateCategory();
    // Load categories for template section dropdown
    api.get<{ Id: number; Name: string }[]>('/categories').then(r => { if (r.success && r.data) setTemplateCategories(r.data); }).catch(() => {});
    // Load fragments (all clause library entries)
    fetch(`${API_URL}/clause-library`, { headers: { ...getAuthHeaders() } })
      .then(r => r.json()).then(d => setFragments(d.data || [])).catch(() => {});
    getConditionalTags().then(tags => {
      setConditionalTags(tags);
      const toggles: Record<string, boolean> = {};
      tags.forEach(t => { toggles[t.tag] = true; });
      setConditionToggles(toggles);
    });
    // Restore document type from Word properties
    Word.run(async (context) => {
      const props = context.document.properties.customProperties;
      props.load('items');
      await context.sync();
      const groupProp = props.items.find((p: any) => p.key === 'dp_docTypeGroup');
      const typeProp = props.items.find((p: any) => p.key === 'dp_docType');
      if (groupProp && typeProp) {
        setSelectedDocType({ group: groupProp.value, type: typeProp.value });
      }
    }).catch(() => {});
  }, []);

  // When condition editor is triggered from createMode, reset editingCondition
  useEffect(() => {
    if (createMode === 'advanced-condition') setEditingCondition(null);
  }, [createMode]);

  const handleInsert = async (key: string) => {
    try {
      // Find label from catalog
      const ph = placeholders.find(p => p.key === key);
      const label = ph?.label || key;
      // Insert as a floating text box (DocuSign-style) — sits on top of the
      // document without disturbing text layout. User drags it in Word to
      // position. Type drives the chip color.
      await insertFloatingPlaceholder(key, label, ph?.type);
      setInsertedTag(key);
      setTimeout(() => setInsertedTag(null), 2000);
      scanDocument();
    } catch (err) {
      // Surface the error to the console + as an alert so silent failures
      // don't leave the user wondering why the click did nothing.
      console.error('[insertFloatingPlaceholder] failed:', err);
      try {
        const msg = (err as any)?.debugInfo?.message
          || (err as any)?.message
          || String(err);
        setError(`Insert failed: ${msg}`);
      } catch { /* ignore */ }
    }
  };

  const handleDragStart = (e: React.DragEvent, key: string) => {
    e.dataTransfer.setData('text/plain', `{${key}}`);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleCreatePlaceholder = async () => {
    if (!newLabel.trim()) return;
    setCreating(true);
    try {
      const name = newLabel.trim();
      const body: Record<string, unknown> = {
        // Name drives both key and label — the form only exposes Name, not Key.
        key: name,
        label: name,
        type: newType,
        required: newRequired,
        sampleValue: newSample.trim() || '',
        source: 'addin',
        description: newDescription.trim(),
      };
      if (newDefault.trim()) {
        // Check if it's a special keyword
        const lower = newDefault.trim().toLowerCase();
        if (lower === 'today' || lower === 'now') {
          body.defaultExpression = lower;
        } else {
          body.defaultValue = newDefault.trim();
        }
      }
      const resp = await api.post<Record<string, unknown>>('/placeholders', body);
      if (resp.success && resp.data) {
        const ph = resp.data as { Id?: number; id?: number };
        const phId = ph.Id || ph.id;

        // Parse and save all NLP rules
        const allRuleTexts = [...newRules];
        if (newRule.trim()) allRuleTexts.push(newRule.trim());

        if (allRuleTexts.length > 0 && phId) {
          for (const ruleText of allRuleTexts) {
            try {
              const parseResp = await fetch(`${API_URL}/placeholder-rules/parse-nlp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ placeholderId: phId, description: ruleText }),
              });
              const parseData = await parseResp.json();
              const rules = parseData.data?.rules || [];
              for (const rule of rules) {
                await fetch(`${API_URL}/placeholder-rules`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                  body: JSON.stringify({
                    placeholderId: phId,
                    ruleType: rule.ruleType,
                    ruleValue: rule.ruleValue,
                    errorMessage: rule.errorMessage,
                    nlpDescription: ruleText,
                  }),
                });
              }
            } catch { /* rule save is best-effort */ }
          }
        }

        setPlaceholders(prev => [...prev, normalizePlaceholder(resp.data!)]);
        setNewDescription(''); setNewLabel(''); setNewType('Text');
        setNewRequired(false); setNewSample(''); setNewDefault(''); setNewRule(''); setNewRules([]);
        setCreateError(null);
        onCloseCreate();
      } else {
        setCreateError((resp.error as any)?.message || 'Failed to create placeholder.');
      }
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.includes('already exists')) {
        setCreateError(msg);
      } else {
        setCreateError('Failed to create placeholder. Please try again.');
      }
    }
    finally { setCreating(false); }
  };

  const handleDeleteCondition = async (id: number) => {
    try {
      await api.delete(`/advanced-conditions/${id}`);
      setConditions(prev => prev.filter(c => c.Id !== id));
    } catch { /* ignore */ }
  };

  const docTagSet = new Set(docTags.map(t => t.tag));

  const filteredCatalog = placeholders.filter(p =>
    (p.key || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.label || '').toLowerCase().includes(search.toLowerCase())
  );

  // Deduplicate by tag — show each placeholder once with count
  const uniqueDocTags: (ContentControlTag & { count: number })[] = [];
  const tagCounts = new Map<string, number>();
  for (const t of docTags) {
    tagCounts.set(t.tag, (tagCounts.get(t.tag) || 0) + 1);
  }
  const seenTags = new Set<string>();
  for (const t of docTags) {
    if (!seenTags.has(t.tag)) {
      seenTags.add(t.tag);
      uniqueDocTags.push({ ...t, count: tagCounts.get(t.tag) || 1 });
    }
  }
  const filteredDocTags = uniqueDocTags.filter(t =>
    !search ||
    t.tag.toLowerCase().includes(search.toLowerCase()) ||
    (t.title || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="dp-ph">

      {/* ── Placeholder create form ── */}
      {createMode === 'placeholder' && (
        <div className="dp-ph__create-form">
          <div className="dp-ph__form-header">
            <span className="dp-ph__form-title">New Placeholder</span>
            <button className="dp-ph__form-close" onClick={onCloseCreate}>✕</button>
          </div>
          <div className="dp-ph__form-row">
            <input className="dp-ph__input" placeholder="Name" value={newLabel}
              onChange={e => {
                setNewLabel(e.target.value);
                setCreateError(null);
                const dup = placeholders.find(p => p.label?.toLowerCase() === e.target.value.trim().toLowerCase());
                if (dup) setCreateError(`A placeholder named "${dup.label}" already exists.`);
              }}
              style={createError ? { borderColor: '#dc2626' } : undefined}
            />
            <input className="dp-ph__input" placeholder="Description" value={newDescription} onChange={e => setNewDescription(e.target.value)} />
          </div>
          <div className="dp-ph__form-row">
            <select className="dp-ph__select" value={newType} onChange={e => setNewType(e.target.value as PlaceholderType)}>
              {PLACEHOLDER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input className="dp-ph__input" placeholder="Default value (e.g. TBD, today)" value={newDefault} onChange={e => setNewDefault(e.target.value)} />
          </div>
          <div className="dp-ph__form-row">
            <input className="dp-ph__input" placeholder="Rule (e.g. max 100 characters)" value={newRule}
              onChange={e => setNewRule(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && newRule.trim()) {
                  e.preventDefault();
                  setNewRules(prev => [...prev, newRule.trim()]);
                  setNewRule('');
                }
              }}
              style={{ flex: 1 }} />
            <button className="dp-ph__btn" onClick={() => {
              if (newRule.trim()) { setNewRules(prev => [...prev, newRule.trim()]); setNewRule(''); }
            }} disabled={!newRule.trim()} style={{ fontSize: 11 }}>+ Add</button>
          </div>
          {newRules.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
              {newRules.map((r, i) => (
                <span key={i} style={{ fontSize: 10, background: '#e0e7ff', color: '#3730a3', padding: '2px 6px', borderRadius: 8, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  {r}
                  <button onClick={() => setNewRules(prev => prev.filter((_, j) => j !== i))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 10, padding: 0, lineHeight: 1 }}>x</button>
                </span>
              ))}
            </div>
          )}
          <div className="dp-ph__form-row">
            <label className="dp-ph__checkbox-label">
              <input type="checkbox" checked={newRequired} onChange={e => setNewRequired(e.target.checked)} />
              Required
            </label>
            <button className="dp-ph__btn dp-ph__btn--primary" onClick={() => { setCreateError(null); handleCreatePlaceholder(); }}
              disabled={creating || !newLabel.trim() || !!createError}>
              {creating ? 'Creating...' : 'Create'}
            </button>
          </div>
          {createError && (
            <div style={{ background: '#fef2f2', color: '#991b1b', fontSize: 11, padding: '6px 8px', borderRadius: 4, border: '1px solid #fecaca', marginTop: 4 }}>
              {createError}
            </div>
          )}
        </div>
      )}

      {/* ── Keyword selector ── */}
      {createMode === 'keyword' && (
        <KeywordForm
          onClose={onCloseCreate}
          onSaved={(keywords) => { setSavedKeywords(keywords); }}
          templateId={activeTemplate?.id}
          currentKeywords={savedKeywords}
        />
      )}

      {/* ── Document Type selector ── */}
      {(createMode === 'document-type' || changingDocType) && (
        <DocumentTypeSelector
          groupedTypes={groupedTypes}
          loading={dtLoading}
          currentSelection={selectedDocType}
          onSelect={(sel) => {
            setSelectedDocType(sel);
            // Persist to Word document properties
            Word.run(async (context) => {
              if (sel) {
                context.document.properties.customProperties.add('dp_docTypeGroup', sel.group);
                context.document.properties.customProperties.add('dp_docType', sel.type);
              } else {
                context.document.properties.customProperties.add('dp_docTypeGroup', '');
                context.document.properties.customProperties.add('dp_docType', '');
              }
              await context.sync();
            }).catch(() => {});
          }}
          onClose={() => { onCloseCreate(); setChangingDocType(false); }}
        />
      )}

      {/* ── Advanced condition builder ── */}
      <div id="condition-form-anchor" />
      {(createMode === 'advanced-condition' || editingCondition) && (
        <AdvancedConditionForm
          placeholders={placeholders}
          editing={editingCondition}
          onClose={() => { setEditingCondition(null); if (createMode === 'advanced-condition') onCloseCreate(); }}
          onSaved={loadConditions}
          templateId={activeTemplate?.id}
        />
      )}

      {/* ════════════════════════════════════════════════════
          SECTION 1 — Placeholders
      ════════════════════════════════════════════════════ */}
      <button className="dp-ph__section-toggle" onClick={() => setPhSectionOpen(o => !o)}>
        <span className="dp-ph__section-chevron">{phSectionOpen ? '▾' : '▸'}</span>
        <span className="dp-ph__section-label">Placeholders</span>
        <span className="dp-ph__section-counts">
          <span className="dp-ph__count-chip">{uniqueDocTags.length} in doc</span>
          <span className="dp-ph__count-chip">{placeholders.length} catalog</span>
        </span>
      </button>

      {phSectionOpen && (
        <div className="dp-ph__section-body">
          <div className="dp-ph__tabs">
            <button
              className={`dp-ph__tab ${tab === 'document' ? 'dp-ph__tab--active' : ''}`}
              onClick={() => { setTab('document'); scanDocument(); }}
            >
              In Document ({uniqueDocTags.length})
            </button>
            <button
              className={`dp-ph__tab ${tab === 'catalog' ? 'dp-ph__tab--active' : ''}`}
              onClick={() => setTab('catalog')}
            >
              Catalog ({placeholders.length})
            </button>
          </div>

          {tab === 'document' && (
            <div className="dp-ph__document">
              <div className="dp-ph__toolbar">
                <input className="dp-ph__search" type="text" placeholder="Search placeholders..."
                  value={search} onChange={e => setSearch(e.target.value)} />
                <button className="dp-ph__btn dp-ph__btn--primary" onClick={scanDocument} disabled={docLoading}>
                  {docLoading ? '…' : 'Scan'}
                </button>
              </div>
              {!docLoading && docTags.length === 0 && (
                <div className="dp-ph__empty">No placeholders found. Switch to Catalog to insert.</div>
              )}
              <div className="dp-ph__list">
                {docLoading && <div className="dp-ph__status">Scanning document...</div>}
                {filteredDocTags.map((t, i) => {
                  const isRepeat = t.tag.startsWith('REPEAT:');
                  const isConditional = t.tag.startsWith('IF:') || t.tag.startsWith('UNLESS:');
                  const isFragment = t.tag.startsWith('fragment.');
                  const isAdvancedCondition = t.tag.startsWith('condition.');
                  const cleanTag = t.tag.replace(/^(REPEAT:|IF:|UNLESS:)/, '');
                  const match = placeholders.find(p => p.key === cleanTag || p.key === t.tag);
                  return (
                    <div key={`${t.tag}-${i}`} className="dp-ph__item">
                      <div className="dp-ph__item-main">
                        <span className="dp-ph__item-key">{`{${t.tag}}`}</span>
                        <span className="dp-ph__item-label">
                          {match ? match.label : t.title || 'Unregistered'}
                          {t.count > 1 && <span style={{ fontSize: 10, color: '#6b7280', marginLeft: 4 }}>({t.count}x)</span>}
                        </span>
                      </div>
                      <div className="dp-ph__item-meta">
                        {isRepeat && <span style={{ fontSize: 9, fontWeight: 600, background: '#dbeafe', color: '#1d4ed8', padding: '1px 5px', borderRadius: 3 }}>REPEAT</span>}
                        {isConditional && <span style={{ fontSize: 9, fontWeight: 600, background: '#fef3c7', color: '#92400e', padding: '1px 5px', borderRadius: 3 }}>{t.tag.startsWith('IF:') ? 'IF' : 'UNLESS'}</span>}
                        {match ? (
                          <span className={`dp-ph__type-badge dp-ph__type-badge--${(match.type || 'text').toLowerCase()}`}>{match.type}</span>
                        ) : isFragment ? (
                          <span style={{ fontSize: 9, fontWeight: 600, background: '#fff7ed', color: '#9a3412', padding: '1px 5px', borderRadius: 3 }}>FRAGMENT</span>
                        ) : isAdvancedCondition ? (
                          <span style={{ fontSize: 9, fontWeight: 600, background: '#ecfdf5', color: '#047857', padding: '1px 5px', borderRadius: 3 }}>ADV CONDITION</span>
                        ) : (
                          <span className="dp-ph__type-badge dp-ph__type-badge--unknown">?</span>
                        )}
                      </div>
                      {match && (
                        <>
                          <button className="dp-ph__btn" onClick={() => openDefaultPopup(match)}
                            title="Set default value" style={{ fontSize: 11, padding: '2px 5px', color: ((match as any).defaultValue || (match as any).DefaultValue || (match as any).defaultExpression || (match as any).DefaultExpression) ? '#059669' : '#d1d5db' }}>
                            &#9733;
                          </button>
                          <button className="dp-ph__btn" onClick={() => togglePhDetails((match as any).id || (match as any).Id)}
                            title="View rules" style={{ fontSize: 11, padding: '2px 5px', color: (rulesMap[(match as any).id || (match as any).Id] || []).length > 0 ? '#f59e0b' : '#d1d5db' }}>
                            &#9889;
                          </button>
                        </>
                      )}
                      <button className="dp-ph__btn" onClick={async () => {
                        try {
                          await Word.run(async (context) => {
                            // 1. Remove matching content controls (SDTs)
                            const ccs = context.document.contentControls;
                            ccs.load('items');
                            await context.sync();
                            for (const cc of ccs.items) {
                              cc.load('tag,text');
                            }
                            await context.sync();
                            for (const cc of ccs.items) {
                              if (cc.tag === t.tag) {
                                const text = cc.text || '';
                                cc.insertText(text, 'Replace');
                                cc.delete(false);
                              }
                            }
                            await context.sync();

                            // 2. Remove floating shapes (chips) whose inner
                            // text is `{tag}` — rewrite the body OOXML with
                            // those shape elements stripped. Covers both
                            // visible and off-screen/hidden chips.
                            const body = context.document.body;
                            const bodyXmlResult = body.getOoxml();
                            await context.sync();
                            let ooxml = bodyXmlResult.value || '';
                            const tagEsc = t.tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                            // Legacy VML chip — bare <v:shape> / <v:roundrect>
                            const shapeRe = new RegExp(
                              `<v:(shape|roundrect|rect|oval)\\b[^>]*>(?:(?!</v:\\1>)[\\s\\S])*?<w:t[^>]*>\\s*\\{${tagEsc}\\}\\s*</w:t>(?:(?!</v:\\1>)[\\s\\S])*?</v:\\1>`,
                              'g',
                            );
                            // Legacy VML chip wrapped in <w:pict>…</w:pict>
                            const pictRe = new RegExp(
                              `<w:pict>\\s*` +
                              `<v:(shape|roundrect|rect|oval)\\b[^>]*>(?:(?!</v:\\1>)[\\s\\S])*?<w:t[^>]*>\\s*\\{${tagEsc}\\}\\s*</w:t>(?:(?!</v:\\1>)[\\s\\S])*?</v:\\1>` +
                              `\\s*</w:pict>`,
                              'g',
                            );
                            // Modern DrawingML chip — <w:drawing>…</w:drawing>
                            // hosting our {tag} text inside its txbxContent.
                            const drawingRe = new RegExp(
                              `<w:drawing\\b[^>]*>(?:(?!</w:drawing>)[\\s\\S])*?<w:t[^>]*>\\s*\\{${tagEsc}\\}\\s*</w:t>(?:(?!</w:drawing>)[\\s\\S])*?</w:drawing>`,
                              'g',
                            );
                            const updated = ooxml
                              .replace(pictRe, '')
                              .replace(shapeRe, '')
                              .replace(drawingRe, '');

                            // 3. Remove leftover plain-text `{tag}` tokens
                            // (e.g. from failed Artemis accepts).
                            const plainRe = new RegExp(`\\{${tagEsc}\\}`, 'g');
                            const finalXml = updated.replace(plainRe, '');

                            if (finalXml !== ooxml) {
                              body.insertOoxml(finalXml, Word.InsertLocation.replace);
                              await context.sync();
                            }
                          });
                          scanDocument();
                        } catch { /* ignore */ }
                      }} title="Remove from document (content controls, floating chips, and plain-text tokens)" style={{ fontSize: 11, padding: '2px 5px', color: '#ef4444' }}>
                        &#10005;
                      </button>
                    </div>
                  );
                })}
              </div>
              {uniqueDocTags.length > 0 && (
                <div className="dp-ph__summary">{uniqueDocTags.length} placeholder{uniqueDocTags.length !== 1 ? 's' : ''}{docTags.length > uniqueDocTags.length ? ` · ${docTags.length} instances` : ''}</div>
              )}
            </div>
          )}

          {tab === 'catalog' && (
            <div className="dp-ph__catalog">
              <div className="dp-ph__toolbar">
                <input className="dp-ph__search" type="text" placeholder="Search placeholders..."
                  value={search} onChange={e => setSearch(e.target.value)} />
                <button className="dp-ph__btn dp-ph__btn--icon" onClick={loadCatalog} title="Refresh">↻</button>
                <button
                  className="dp-ph__btn dp-ph__btn--icon"
                  title="Restyle all placeholders in the document to match current colors"
                  onClick={async () => {
                    const typeMap = Object.fromEntries(
                      placeholders.map(ph => [ph.key, (ph.type as string) || 'Text']),
                    );
                    try {
                      await restyleAllPlaceholders(typeMap);
                    } catch { /* ignore */ }
                  }}
                >🎨</button>
              </div>
              {catalogLoading && <div className="dp-ph__status">Loading...</div>}
              {catalogError && <div className="dp-ph__error">{catalogError}</div>}
              <div className="dp-ph__drag-hint">
                Click or drag a placeholder to insert at cursor.
                <br />
                <span style={{ fontSize: 10, color: '#6b7280' }}>
                  Tip: After insert, click the chip in Word and use Arrow keys to nudge 1pt at a time.
                </span>
              </div>
              <div className="dp-ph__list">
                {filteredCatalog.map(p => {
                  const phId = (p as any).id || (p as any).Id;
                  const defVal = (p as any).defaultValue || (p as any).DefaultValue;
                  const defExpr = (p as any).defaultExpression || (p as any).DefaultExpression;
                  const hasDefault = !!(defVal || defExpr);
                  const isExpanded = expandedPh === phId;
                  const phRules = rulesMap[phId] || [];

                  return (
                  <div key={p.id}>
                    <div
                      className={`dp-ph__item dp-ph__item--clickable${insertedTag === p.key ? ' dp-ph__item--inserted' : ''}`}
                      draggable={true}
                      onDragStart={(e) => {
                        // Native HTML5 drag → Word accepts the dropped text at the
                        // drop position. Plain-text token, no content control.
                        e.dataTransfer.setData('text/plain', `{${p.key}}`);
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                      onDragEnd={() => {
                        // Word commits the drop async; instead of a fixed delay
                        // (which leaves the raw {tag} text flickering on screen),
                        // poll for the token's appearance and swap it the moment
                        // Word has it. Typical convert-time: 30-60 ms.
                        const typeMap = Object.fromEntries(
                          placeholders.map(ph => [ph.key, (ph.type as string) || 'Text']),
                        );
                        const expected = `{${p.key}}`;
                        const deadline = Date.now() + 1500;
                        const tick = async () => {
                          try {
                            const converted = await convertDroppedPlaceholders(typeMap, expected);
                            if (converted > 0 || Date.now() > deadline) return;
                          } catch { /* keep polling */ }
                          setTimeout(tick, 30);
                        };
                        // Kick off immediately — first sync usually runs before
                        // Word paints the inline text, so the user sees the chip
                        // appear directly rather than text → chip.
                        tick();
                      }}
                      onClick={() => handleInsert(p.key)}
                      title="Click to insert a floating placeholder, or drag onto the document to drop at a specific spot."
                    >
                      <div className="dp-ph__drag-handle" style={{ cursor: 'grab' }}>⋮⋮</div>
                      <div className="dp-ph__item-main">
                        <span className="dp-ph__item-key">{`{${p.key}}`}</span>
                        {p.description && (
                          <span className="dp-ph__item-description" style={{ fontSize: 10, color: '#6b7280', marginTop: 2, display: 'block' }}>
                            {p.description}
                          </span>
                        )}
                      </div>
                      <div className="dp-ph__item-meta" style={{ position: 'relative' }}>
                        {auth.hasPermission('master:write') ? (
                          <span
                            className={`dp-ph__type-badge dp-ph__type-badge--${(p.type || 'text').toLowerCase()}`}
                            title="Click to change the placeholder type"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setTypePickerPh(typePickerPh === phId ? null : (phId ?? null));
                            }}
                          >
                            {p.type}
                          </span>
                        ) : (
                          <span className={`dp-ph__type-badge dp-ph__type-badge--${(p.type || 'text').toLowerCase()}`}>
                            {p.type}
                          </span>
                        )}
                        {p.required && <span className="dp-ph__required-badge">Req</span>}
                        {docTagSet.has(p.key) && <span className="dp-ph__in-doc-badge" title="In document">✓</span>}
                        {typePickerPh === phId && (
                          <>
                            <div
                              style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9 }}
                              onClick={() => setTypePickerPh(null)}
                            />
                            <div style={{
                              position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 10,
                              background: '#fff', border: '1px solid #d1d5db', borderRadius: 4,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 110,
                            }}>
                              {PLACEHOLDER_TYPES.map(t => (
                                <button
                                  key={t}
                                  style={{
                                    display: 'block', width: '100%', textAlign: 'left',
                                    padding: '6px 10px', fontSize: 11, border: 'none',
                                    background: t === p.type ? '#f3f4f6' : '#fff', cursor: 'pointer',
                                  }}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    setTypePickerPh(null);
                                    if (!phId || t === p.type) return;
                                    try {
                                      const resp = await api.patch<Record<string, unknown>>(`/placeholders/${phId}`, { type: t });
                                      if (resp.success && resp.data) {
                                        setPlaceholders(prev => prev.map(x =>
                                          ((x as any).Id || (x as any).id) === phId
                                            ? normalizePlaceholder(resp.data!)
                                            : x,
                                        ));
                                      }
                                    } catch { /* ignore */ }
                                  }}
                                >
                                  {t}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                      {/* Default value icon */}
                      <div style={{ position: 'relative' }}>
                        <Can permission="master:write">
                          <button className="dp-ph__btn" onClick={(e) => { e.stopPropagation(); openDefaultPopup(p); }}
                            title="Set default value" style={{ fontSize: 11, padding: '2px 5px', color: hasDefault ? '#059669' : '#d1d5db' }}>
                            &#9733;
                          </button>
                        </Can>
                        {defaultPopupPh === phId && (
                          <>
                            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9 }}
                              onClick={(e) => { e.stopPropagation(); setDefaultPopupPh(null); }} />
                            <div style={{
                              position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 10,
                              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
                              boxShadow: '0 12px 40px rgba(0,0,0,0.2)', padding: '16px',
                              width: 300, fontSize: 12,
                            }} onClick={(e) => e.stopPropagation()}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, borderBottom: '1px solid #f1f5f9', paddingBottom: 8 }}>
                                <div>
                                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{p.label}</div>
                                  <div style={{ fontSize: 10, color: '#94a3b8' }}>Set default value</div>
                                </div>
                                <button onClick={(e) => { e.stopPropagation(); setDefaultPopupPh(null); }}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#94a3b8' }}>✕</button>
                              </div>
                              <div style={{ marginBottom: 8 }}>
                                <div style={{ fontWeight: 600, color: '#475569', fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>Default Value</div>
                                <input className="dp-ph__input" value={defaultPopupValue}
                                  onChange={e => setDefaultPopupValue(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') saveDefault(); e.stopPropagation(); }}
                                  onClick={e => e.stopPropagation()}
                                  placeholder="e.g. TBD, N/A, today, now"
                                  style={{ width: '100%', fontSize: 12 }} />
                                <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 2 }}>
                                  Special: "today" = current date, "now" = current datetime
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                <button className="dp-ph__btn" onClick={(e) => { e.stopPropagation(); setDefaultPopupPh(null); }}
                                  style={{ fontSize: 11 }}>Cancel</button>
                                <button className="dp-ph__btn dp-ph__btn--primary" onClick={(e) => { e.stopPropagation(); saveDefault(); }}
                                  disabled={defaultPopupSaving} style={{ fontSize: 11 }}>
                                  {defaultPopupSaving ? '...' : 'Save'}
                                </button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      {/* Rules icon */}
                      <div style={{ position: 'relative' }}>
                        <button className="dp-ph__btn" onClick={(e) => { e.stopPropagation(); togglePhDetails(phId); }}
                          title="View rules" style={{ fontSize: 11, padding: '2px 5px', color: isExpanded ? '#3b82f6' : phRules.length > 0 ? '#f59e0b' : '#d1d5db' }}>
                          &#9889;
                        </button>
                        {isExpanded && (
                          <>
                            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9 }}
                              onClick={(e) => { e.stopPropagation(); setExpandedPh(null); }} />
                            <div style={{
                              position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 10,
                              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
                              boxShadow: '0 12px 40px rgba(0,0,0,0.2)', padding: '16px',
                              width: 320, maxHeight: '80vh', overflowY: 'auto', fontSize: 12,
                            }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, borderBottom: '1px solid #f1f5f9', paddingBottom: 8 }}>
                                <div>
                                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{p.label}</div>
                                  <div style={{ fontSize: 10, color: '#94a3b8' }}>{p.key} · {p.type}</div>
                                </div>
                                <button onClick={(e) => { e.stopPropagation(); setExpandedPh(null); }}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#94a3b8', padding: '2px 6px' }}>✕</button>
                              </div>

                              {/* Rules */}
                              <div style={{ marginBottom: 10 }}>
                                <div style={{ fontWeight: 600, color: '#475569', fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>
                                  Validation Rules ({phRules.length})
                                </div>
                                {phRules.length > 0 ? phRules.map((r, i) => (
                                  <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', padding: '4px 0', borderBottom: '1px solid #f8fafc' }}>
                                    <span style={{ background: '#e0e7ff', color: '#4338ca', padding: '1px 6px', borderRadius: 3, fontSize: 9, fontWeight: 600, flexShrink: 0 }}>{r.RuleType}</span>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ color: '#374151' }}>{r.ErrorMessage || r.NlpDescription}</div>
                                      {r.RuleValue && <div style={{ color: '#9ca3af', fontSize: 10 }}>Value: {r.RuleValue}</div>}
                                    </div>
                                    <button onClick={async (e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      const ruleId = (r as any).Id || (r as any).id;
                                      if (!ruleId) return;
                                      try {
                                        const resp = await fetch(`${API_URL}/placeholder-rules/${ruleId}`, {
                                          method: 'DELETE', headers: { ...getAuthHeaders() },
                                        });
                                        if (resp.ok) {
                                          setRulesMap(prev => ({ ...prev, [phId]: (prev[phId] || []).filter((rule: any) => (rule.Id || rule.id) !== ruleId) }));
                                        }
                                      } catch {}
                                    }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 12, padding: '2px 4px', fontWeight: 'bold' }} title="Delete rule">✕</button>
                                  </div>
                                )) : (
                                  <div style={{ color: '#9ca3af', fontSize: 11, padding: '4px 0' }}>No rules defined</div>
                                )}
                              </div>

                              {/* Add Rule */}
                              <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
                                <div style={{ fontWeight: 600, color: '#475569', fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>Add Rule</div>
                                <div style={{ display: 'flex', gap: 4 }}>
                                  <input
                                    className="dp-ph__input"
                                    placeholder="e.g. max 100 characters"
                                    value={popupRuleInput}
                                    onChange={e => setPopupRuleInput(e.target.value)}
                                    onKeyDown={async (e) => {
                                      if (e.key === 'Enter' && popupRuleInput.trim()) {
                                        e.stopPropagation();
                                        await addRuleFromPopup(phId, popupRuleInput.trim());
                                      }
                                    }}
                                    onClick={e => e.stopPropagation()}
                                    style={{ flex: 1, fontSize: 11 }}
                                  />
                                  <button className="dp-ph__btn dp-ph__btn--primary"
                                    onClick={async (e) => { e.stopPropagation(); await addRuleFromPopup(phId, popupRuleInput.trim()); }}
                                    disabled={!popupRuleInput.trim() || popupRuleSaving}
                                    style={{ fontSize: 10, padding: '3px 8px' }}>
                                    {popupRuleSaving ? '...' : 'Add'}
                                  </button>
                                </div>
                                {popupRuleError && (
                                  <div style={{ color: '#dc2626', fontSize: 10, marginTop: 4 }}>{popupRuleError}</div>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      <button className="dp-ph__btn dp-ph__btn--insert" onClick={(e) => { e.stopPropagation(); handleInsert(p.key); }}>Insert</button>
                    </div>
                  </div>
                  );
                })}
                {!catalogLoading && filteredCatalog.length === 0 && (
                  <div className="dp-ph__empty">
                    {search ? 'No placeholders match your search.' : 'No placeholders yet.'}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Global Default Value Popup (works from both In Document & Catalog tabs) ── */}
      {defaultPopupPh && tab === 'document' && (() => {
        const popupPh = placeholders.find(p => (p as any).id === defaultPopupPh || (p as any).Id === defaultPopupPh);
        if (!popupPh) return null;
        return (
          <>
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9 }}
              onClick={() => setDefaultPopupPh(null)} />
            <div style={{
              position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 10,
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
              boxShadow: '0 12px 40px rgba(0,0,0,0.2)', padding: '16px', width: 300, fontSize: 12,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, borderBottom: '1px solid #f1f5f9', paddingBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{popupPh.label}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8' }}>Set default value</div>
                </div>
                <button onClick={() => setDefaultPopupPh(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#94a3b8' }}>✕</button>
              </div>
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontWeight: 600, color: '#475569', fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>Default Value</div>
                <input className="dp-ph__input" value={defaultPopupValue}
                  onChange={e => setDefaultPopupValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveDefault(); }}
                  placeholder="e.g. TBD, N/A, today, now"
                  style={{ width: '100%', fontSize: 12 }} />
                <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 2 }}>
                  Special: "today" = current date, "now" = current datetime
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <button className="dp-ph__btn" onClick={() => setDefaultPopupPh(null)} style={{ fontSize: 11 }}>Cancel</button>
                <button className="dp-ph__btn dp-ph__btn--primary" onClick={() => saveDefault()}
                  disabled={defaultPopupSaving} style={{ fontSize: 11 }}>
                  {defaultPopupSaving ? '...' : 'Save'}
                </button>
              </div>
            </div>
          </>
        );
      })()}

      {/* ── Global Rules Popup (works from both In Document & Catalog tabs) ── */}
      {expandedPh && tab === 'document' && (() => {
        const rulesPh = placeholders.find(p => (p as any).id === expandedPh || (p as any).Id === expandedPh);
        if (!rulesPh) return null;
        const phId = expandedPh;
        const phRules = rulesMap[phId] || [];
        return (
          <>
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9 }}
              onClick={() => setExpandedPh(null)} />
            <div style={{
              position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 10,
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
              boxShadow: '0 12px 40px rgba(0,0,0,0.2)', padding: '16px',
              width: 320, maxHeight: '80vh', overflowY: 'auto', fontSize: 12,
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, borderBottom: '1px solid #f1f5f9', paddingBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{rulesPh.label}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8' }}>{rulesPh.key} · {rulesPh.type}</div>
                </div>
                <button onClick={() => setExpandedPh(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#94a3b8', padding: '2px 6px' }}>✕</button>
              </div>

              {/* Rules */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 600, color: '#475569', fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>
                  Validation Rules ({phRules.length})
                </div>
                {phRules.length > 0 ? phRules.map((r: any, i: number) => (
                  <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', padding: '4px 0', borderBottom: '1px solid #f8fafc' }}>
                    <span style={{ background: '#e0e7ff', color: '#4338ca', padding: '1px 6px', borderRadius: 3, fontSize: 9, fontWeight: 600, flexShrink: 0 }}>{r.RuleType}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#374151' }}>{r.ErrorMessage || r.NlpDescription}</div>
                      {r.RuleValue && <div style={{ color: '#9ca3af', fontSize: 10 }}>Value: {r.RuleValue}</div>}
                    </div>
                    <button onClick={async (e) => {
                      e.stopPropagation();
                      const ruleId = r.Id || r.id;
                      if (!ruleId) return;
                      try {
                        const resp = await fetch(`${API_URL}/placeholder-rules/${ruleId}`, {
                          method: 'DELETE', headers: { ...getAuthHeaders() },
                        });
                        if (resp.ok) {
                          setRulesMap(prev => ({ ...prev, [phId]: (prev[phId] || []).filter((rule: any) => (rule.Id || rule.id) !== ruleId) }));
                        }
                      } catch {}
                    }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 12, padding: '2px 4px', fontWeight: 'bold' }} title="Delete rule">✕</button>
                  </div>
                )) : (
                  <div style={{ color: '#9ca3af', fontSize: 11, padding: '4px 0' }}>No rules defined</div>
                )}
              </div>

              {/* Add Rule */}
              <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
                <div style={{ fontWeight: 600, color: '#475569', fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>Add Rule</div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <input
                    className="dp-ph__input"
                    placeholder="e.g. max 100 characters"
                    value={popupRuleInput}
                    onChange={e => setPopupRuleInput(e.target.value)}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' && popupRuleInput.trim()) {
                        e.stopPropagation();
                        await addRuleFromPopup(phId, popupRuleInput.trim());
                      }
                    }}
                    onClick={e => e.stopPropagation()}
                    style={{ flex: 1, fontSize: 11 }}
                  />
                  <button className="dp-ph__btn dp-ph__btn--primary"
                    onClick={async (e) => { e.stopPropagation(); await addRuleFromPopup(phId, popupRuleInput.trim()); }}
                    disabled={!popupRuleInput.trim() || popupRuleSaving}
                    style={{ fontSize: 10, padding: '3px 8px' }}>
                    {popupRuleSaving ? '...' : 'Add'}
                  </button>
                </div>
                {popupRuleError && (
                  <div style={{ color: '#dc2626', fontSize: 10, marginTop: 4 }}>{popupRuleError}</div>
                )}
              </div>
            </div>
          </>
        );
      })()}

      {/* ════════════════════════════════════════════════════
          SECTION 2 — Keywords
      ════════════════════════════════════════════════════ */}
      <button className="dp-ph__section-toggle" onClick={() => setKwSectionOpen(o => !o)}>
        <span className="dp-ph__section-chevron">{kwSectionOpen ? '▾' : '▸'}</span>
        <span className="dp-ph__section-label">Keywords</span>
        <span className="dp-ph__section-counts">
          <span className="dp-ph__count-chip">{savedKeywords.length} selected</span>
        </span>
      </button>

      {kwSectionOpen && (
        <div className="dp-ph__section-body">
          {savedKeywords.length === 0 ? (
            <div className="dp-ph__empty" style={{ padding: '10px 12px' }}>
              No keywords selected. Click <strong>+ New → Keyword</strong> to add keywords.
            </div>
          ) : (
            <div className="dp-ph__list">
              {savedKeywords.map(kw => (
                <div key={kw.Id} className="dp-ph__item">
                  <div className="dp-ph__item-main">
                    <span className="dp-ph__item-key">{kw.Keyword || kw.Name}</span>
                    {(kw.GroupName || kw.DocumentTypeGroup) && (
                      <span className="dp-ph__item-label">{kw.GroupName || kw.DocumentTypeGroup}</span>
                    )}
                  </div>
                  <button
                    className="dp-ph__btn dp-ph__btn--icon"
                    style={{ color: '#c62828', fontSize: 10 }}
                    onClick={async () => {
                      if (activeTemplate?.id) {
                        try { await api.delete(`/keywords/by-template/${activeTemplate.id}/${kw.Id}`); } catch { /* ignore */ }
                      }
                      setSavedKeywords(prev => prev.filter(k => k.Id !== kw.Id));
                    }}
                    title="Remove"
                  >✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          SECTION 3 — Advanced Conditions
      ════════════════════════════════════════════════════ */}
      <button className="dp-ph__section-toggle" onClick={() => setAcSectionOpen(o => !o)}>
        <span className="dp-ph__section-chevron">{acSectionOpen ? '▾' : '▸'}</span>
        <span className="dp-ph__section-label">Advanced Conditions</span>
        <span className="dp-ph__section-counts">
          <span className="dp-ph__count-chip">{conditions.length} saved</span>
        </span>
      </button>

      {acSectionOpen && (
        <div className="dp-ph__section-body">
          {conditionsLoading && <div className="dp-ph__status">Loading...</div>}
          {!conditionsLoading && conditions.length === 0 && (
            <div className="dp-ph__empty" style={{ padding: '10px 12px' }}>
              No conditions yet. Click <strong>+ New → Advanced Condition</strong> to create one.
            </div>
          )}
          {conditions.length > 0 && (
            <div className="dp-ph__list">
              {conditions.map(c => (
                <div key={c.Id} className="dp-ph__item dp-ac__list-item">
                  <div className="dp-ph__item-main">
                    <span className="dp-ph__item-key">{c.Name}</span>
                    <span className="dp-ph__item-label">
                      {c.Field ? `IF ${c.Field} ${c.Operator} "${c.Value || ''}"` : 'No condition set'}
                      {c.ElseText ? ` · else: "${c.ElseText}"` : ''}
                    </span>
                  </div>
                  <div className="dp-ph__item-meta">
                    <button
                      className="dp-ph__btn dp-ph__btn--select"
                      onClick={async () => {
                        const condKey = `condition.${c.Name.replace(/\s+/g, '_').toLowerCase()}`;
                        // Insert as a floating chip — same UX as regular placeholders.
                        // 'Text' type so it picks the yellow theme; the filler will
                        // resolve {condition.foo} via its registered placeholder entry.
                        await insertFloatingPlaceholder(condKey, c.Name, 'Text');
                        scanDocument();
                      }}
                      title="Insert into document"
                    >Insert</button>
                    <button
                      className="dp-ph__btn dp-ph__btn--select"
                      onClick={() => { setEditingCondition(c); onCloseCreate(); setTimeout(() => document.getElementById('condition-form-anchor')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
                      title="Edit"
                    >Edit</button>
                    <button
                      className="dp-ph__btn"
                      style={{ color: '#c62828', borderColor: '#f5c6cb', fontSize: 10, padding: '2px 6px' }}
                      onClick={() => handleDeleteCondition(c.Id)}
                      title="Delete"
                    >✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          SECTION 4 — Document Type
      ════════════════════════════════════════════════════ */}
      <button className="dp-ph__section-toggle" onClick={() => setDtSectionOpen(o => !o)}>
        <span className="dp-ph__section-chevron">{dtSectionOpen ? '▾' : '▸'}</span>
        <span className="dp-ph__section-label">Document Type</span>
        <span className="dp-ph__section-counts">
          {selectedDocType
            ? <span className="dp-ph__count-chip dp-dt__selected-chip">{selectedDocType.type}</span>
            : <span className="dp-ph__count-chip">None selected</span>
          }
        </span>
      </button>

      {dtSectionOpen && (
        <div className="dp-ph__section-body">
          {selectedDocType ? (
            <div className="dp-dt__assigned-row">
              <span className="dp-dt__assigned-group">{selectedDocType.group}</span>
              <span className="dp-dt__assigned-type">{selectedDocType.type}</span>
              <button className="dp-dt__assigned-change" onClick={() => setChangingDocType(true)}>Change</button>
            </div>
          ) : (
            <div className="dp-ph__empty" style={{ padding: '10px 12px' }}>
              No document type assigned. Click <strong>+ New → Document Type</strong> to select one.
            </div>
          )}
        </div>
      )}

      {/* ── Fragments Section (only show if fragments exist) ── */}
      {fragments.length > 0 && (
        <>
          <button className="dp-ph__section-toggle" onClick={() => setFragSectionOpen(o => !o)}>
            <span className="dp-ph__section-chevron">{fragSectionOpen ? '\u25BE' : '\u25B8'}</span>
            <span className="dp-ph__section-label">Fragments</span>
            <div className="dp-ph__section-counts">
              <span className="dp-ph__count-chip">{fragments.length} saved</span>
            </div>
          </button>
          {fragSectionOpen && (
            <div className="dp-ph__section-body">
              <div className="dp-ph__list">
                {fragments.map((f: any) => {
                  const name = f.description || f.standardText || f.StandardText || f.Name || 'Fragment';
                  const category = f.category || f.Category || '';
                  const content = f.standardText || f.StandardText || '';
                  const insertFragment = async () => {
                    try {
                      const fragmentName = (f.standardText || f.StandardText || f.Name || '').trim();
                      const slug = fragmentName.replace(/[^a-zA-Z0-9]/g, '') || String(f.id || f.Id || '');
                      if (slug) {
                        // Chip visible as `{fragment.<slug>}` — server expands
                        // it with current fragment content at generate time.
                        await insertFloatingPlaceholder(`fragment.${slug}`, fragmentName || name, 'Text');
                      } else if (content) {
                        await Word.run(async (context) => {
                          const sel = context.document.getSelection();
                          sel.insertText(content, Word.InsertLocation.after);
                          await context.sync();
                        });
                      }
                    } catch { /* ignore */ }
                  };
                  return (
                    <div key={f.id || f.Id} className="dp-ph__item dp-ph__item--clickable"
                      onClick={insertFragment}
                      title={`Click to insert: ${content.substring(0, 50)}`}
                    >
                      <div className="dp-ph__item-main">
                        <span className="dp-ph__item-key">{name}</span>
                        <span className="dp-ph__item-label">{category}</span>
                      </div>
                      <button
                        className="dp-ph__btn dp-ph__btn--insert"
                        onClick={(e) => { e.stopPropagation(); insertFragment(); }}
                      >
                        Insert
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── AI Quality Check Section ── */}
      <button className="dp-ph__section-toggle" onClick={() => setQcSectionOpen(o => !o)}>
        <span className="dp-ph__section-chevron">{qcSectionOpen ? '\u25BE' : '\u25B8'}</span>
        <span className="dp-ph__section-label">Quality Check</span>
        {qcResult && (
          <span className="dp-ph__count-chip" style={{
            background: qcResult.passed ? '#dcfce7' : '#fef2f2',
            color: qcResult.passed ? '#166534' : '#991b1b',
          }}>
            {qcResult.score}/100
          </span>
        )}
      </button>
      {qcSectionOpen && (
        <div className="dp-ph__section-body" style={{ padding: '8px 12px' }}>
          <button
            className="dp-ph__btn dp-ph__btn--primary"
            onClick={runQualityCheck}
            disabled={qcRunning}
            style={{ width: '100%', marginBottom: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            {qcRunning ? 'Checking...' : '\u2728 Run Quality Check'}
          </button>

          {qcResult && (
            <div>
              {/* Score bar */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, color: qcResult.passed ? '#166534' : '#991b1b' }}>
                    Score: {qcResult.score}/100 — {qcResult.passed ? 'PASSED' : 'NEEDS REVIEW'}
                  </span>
                </div>
                <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${qcResult.score}%`,
                    background: qcResult.score >= 80 ? '#16a34a' : qcResult.score >= 60 ? '#d97706' : '#dc2626',
                    borderRadius: 3,
                    transition: 'width 0.5s ease',
                  }} />
                </div>
              </div>

              {/* Summary */}
              <p style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>{qcResult.summary}</p>

              {/* Issues list */}
              {qcResult.issues.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {qcResult.issues.map((issue: any, i: number) => (
                    <div key={i} style={{
                      padding: '6px 8px',
                      borderRadius: 6,
                      fontSize: 11,
                      lineHeight: 1.4,
                      background: issue.severity === 'error' ? '#fef2f2' : issue.severity === 'warning' ? '#fffbeb' : '#f0f9ff',
                      border: `1px solid ${issue.severity === 'error' ? '#fecaca' : issue.severity === 'warning' ? '#fde68a' : '#bae6fd'}`,
                    }}>
                      <div style={{ fontWeight: 600, color: issue.severity === 'error' ? '#991b1b' : issue.severity === 'warning' ? '#92400e' : '#075985' }}>
                        {issue.severity === 'error' ? '\u274C' : issue.severity === 'warning' ? '\u26A0\uFE0F' : '\u2139\uFE0F'} {issue.category}
                      </div>
                      <div style={{ color: '#374151', marginTop: 2 }}>{issue.message}</div>
                      {issue.suggestion && (
                        <div style={{ color: '#6b7280', fontStyle: 'italic', marginTop: 2 }}>{issue.suggestion}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {qcResult.issues.length === 0 && (
                <div style={{ padding: 12, textAlign: 'center', color: '#166534', fontSize: 12 }}>
                  ✅ No issues found — template quality is excellent!
                </div>
              )}
            </div>
          )}

          {!qcResult && !qcRunning && (
            <p style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
              AI will check for missing placeholders, naming issues, and more.
            </p>
          )}
        </div>
      )}

      {/* ── Template Info Section ── */}
      <button
        className="dp-ph__section-toggle"
        onClick={() => setTplSectionOpen(o => !o)}
      >
        <span className="dp-ph__section-chevron">{tplSectionOpen ? '\u25BE' : '\u25B8'}</span>
        <span className="dp-ph__section-label">Template</span>
        <div className="dp-ph__section-counts">
          <span className="dp-ph__count-chip">{activeTemplate ? `ID: ${activeTemplate.id}` : 'Not saved'}</span>
        </div>
      </button>
      {tplSectionOpen && (
        <div className="dp-ph__section-body">
          {activeTemplate ? (
            <div className="dp-tpl__info">
              <div className="dp-tpl__row">
                <span className="dp-tpl__label">ID</span>
                <span className="dp-tpl__value">{activeTemplate.id}</span>
              </div>
              <div className="dp-tpl__row">
                <span className="dp-tpl__label">Name</span>
                {editingName ? (
                  <div className="dp-tpl__name-edit">
                    <input
                      className="dp-ph__input"
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleNameSave();
                        if (e.key === 'Escape') setEditingName(false);
                      }}
                      autoFocus
                    />
                    <button className="dp-ph__btn dp-ph__btn--primary" onClick={handleNameSave} disabled={nameSaving}>
                      {nameSaving ? '...' : 'Save'}
                    </button>
                    <button className="dp-ph__btn" onClick={() => setEditingName(false)}>Cancel</button>
                  </div>
                ) : (
                  <span className="dp-tpl__value dp-tpl__value--editable" onClick={() => { setEditName(activeTemplate.name); setEditingName(true); }}>
                    {activeTemplate.name} <span className="dp-tpl__edit-icon">[edit]</span>
                  </span>
                )}
              </div>
              <div className="dp-tpl__row">
                <span className="dp-tpl__label">Category</span>
                <select className="dp-ph__select" style={{ flex: 1, fontSize: 12 }}
                  value={templateCategoryId ?? '__none__'}
                  onChange={e => handleCategoryChange(e.target.value)}>
                  <option value="__none__">— None —</option>
                  {templateCategories.map(c => <option key={c.Id} value={c.Id}>{c.Name}</option>)}
                </select>
              </div>
              <div className="dp-tpl__row">
                <span className="dp-tpl__label">Version</span>
                <span className="dp-tpl__value">{activeTemplate.versionStatus} v{activeTemplate.versionNumber}</span>
              </div>
              <div className="dp-tpl__row">
                <span className="dp-tpl__label">Lock</span>
                <span className="dp-tpl__value">{activeTemplate.isLocked ? 'Checked out by you' : 'Not locked'}</span>
              </div>
              {activeTemplate.versionStatus === 'Draft' && (
                <div className="dp-tpl__row" style={{ borderBottom: 'none', paddingTop: 6 }}>
                  <button
                    className="dp-ph__btn"
                    style={{ color: '#c62828', borderColor: '#f5c6cb', fontSize: 11, width: '100%' }}
                    onClick={handleDiscardDraft}
                    disabled={discarding}
                  >
                    {discarding ? 'Discarding...' : 'Discard Draft'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="dp-ph__empty" style={{ padding: '10px 12px' }}>
              No template saved yet. Click <strong>Save</strong> to create one.
            </div>
          )}
        </div>
      )}
    </div>
  );
}


